/**
 * Scheduled booking `visit_at` helpers.
 *
 * Write path: clients still send activity-local date + HH:MM stamped as `…Z`
 * (wall-clock transport). The API converts to a true UTC instant using
 * the activity region (IT→Europe/Rome, ES→Atlantic/Canary).
 *
 * Read path: interpret stored ISO as a real UTC instant in the activity IANA
 * zone (or prefer `time_slot` for the clock label). Returns null when region
 * is unknown — no UTC fallback.
 */

import {
  browserIanaTimeZone,
  calendarDateKeyInTimeZone,
  ianaTimezoneForRegionCode,
  partValue,
} from "@/lib/activityTimezone";
import type { RegionCode } from "@/types";

/** Activity-local `YYYY-MM-DD` for date inputs from a stored UTC instant. */
export function visitAtIsoToDateInput(
  iso: string,
  regionCode: RegionCode | null,
): string | null {
  const timeZone = ianaTimezoneForRegionCode(regionCode);
  if (!timeZone) {
    return null;
  }
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const y = partValue(parts, "year");
  const m = partValue(parts, "month");
  const day = partValue(parts, "day");
  return `${y}-${m}-${day}`;
}

/** Activity-local `HH:mm` from a stored UTC instant (prefer `time_slot` when available). */
export function visitAtIsoToTimeInput(
  iso: string,
  regionCode: RegionCode | null,
): string | null {
  const timeZone = ianaTimezoneForRegionCode(regionCode);
  if (!timeZone) {
    return null;
  }
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  return `${partValue(parts, "hour")}:${partValue(parts, "minute")}`;
}

/** Normalize API schedule times (e.g. "09:00:00") to "HH:mm". */
export function scheduleTimeToHHmm(value: string): string {
  return value.length >= 5 ? value.slice(0, 5) : value;
}

/**
 * Encode activity-local date + HH:mm for the API (wall-clock transport).
 * Server converts to true UTC using the activity region.
 */
export function visitAtLocalPickerToApiIso(dateStr: string, timeHHmm: string): string {
  const hm = timeHHmm.length >= 5 ? timeHHmm.slice(0, 5) : timeHHmm;
  return `${dateStr}T${hm}:00.000Z`;
}

/** Browser-local today as `YYYY-MM-DD` (date picker fallback). */
export function todayBrowserLocalDateInput(): string {
  return calendarDateKeyInTimeZone(new Date(), browserIanaTimeZone());
}

/** Activity-local today for date inputs; browser-local when region is unknown. */
export function todayActivityLocalDateInput(
  regionCode: RegionCode | null,
): string {
  const timeZone = ianaTimezoneForRegionCode(regionCode);
  return timeZone
    ? calendarDateKeyInTimeZone(new Date(), timeZone)
    : todayBrowserLocalDateInput();
}

/** Compact visit line: activity-local date + time_slot (or derived clock). */
export function formatActivityLocalVisitSummary(
  visitAt: string,
  options: {
    regionCode: RegionCode | null;
    timeSlot?: string | null;
    locale?: string;
  },
): string | null {
  const timeZone = ianaTimezoneForRegionCode(options.regionCode);
  if (!timeZone) {
    return null;
  }
  const parsed = new Date(visitAt);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  const date = parsed.toLocaleDateString(options.locale, {
    timeZone,
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const time =
    options.timeSlot?.trim()
      ? scheduleTimeToHHmm(options.timeSlot)
      : visitAtIsoToTimeInput(visitAt, options.regionCode);
  return time ? `${date}, ${time}` : date;
}

/**
 * True when a stored/API `visit_at` instant (true UTC) is still in the future.
 * Use for read-path checks on persisted booking values — not for wall-clock transport.
 */
export function isFutureVisitAtIso(visitAtIso: string): boolean {
  return new Date(visitAtIso).getTime() > Date.now();
}

/**
 * UTC epoch ms for activity-local calendar date + HH:mm (mirrors backend `wall_local_to_utc`).
 * Returns null when the region is unknown or inputs are invalid.
 */
export function utcMillisForActivityLocalVisit(
  dateStr: string,
  timeHHmm: string,
  regionCode: RegionCode,
): number | null {
  const timeZone = ianaTimezoneForRegionCode(regionCode);
  if (!timeZone) {
    return null;
  }

  const hm = scheduleTimeToHHmm(timeHHmm);
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr.trim());
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(hm);
  if (!dateMatch || !timeMatch) {
    return null;
  }

  const year = Number(dateMatch[1]);
  const month = Number(dateMatch[2]);
  const day = Number(dateMatch[3]);
  const hour = Number(timeMatch[1]);
  const minute = Number(timeMatch[2]);
  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    hour > 23 ||
    minute > 59
  ) {
    return null;
  }

  // Start from wall-clock transport (local face stamped as UTC), then converge.
  let utcMs = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  for (let attempt = 0; attempt < 4; attempt++) {
    const parts = formatter.formatToParts(new Date(utcMs));
    const localY = Number(partValue(parts, "year"));
    const localM = Number(partValue(parts, "month"));
    let localD = Number(partValue(parts, "day"));
    let localH = Number(partValue(parts, "hour"));
    const localMin = Number(partValue(parts, "minute"));
    if (localH === 24) {
      localH = 0;
      localD += 1;
    }

    if (
      localY === year &&
      localM === month &&
      localD === day &&
      localH === hour &&
      localMin === minute
    ) {
      return utcMs;
    }

    const desired = Date.UTC(year, month - 1, day, hour, minute);
    const actual = Date.UTC(localY, localM - 1, localD, localH, localMin);
    utcMs += desired - actual;
  }

  return utcMs;
}

/**
 * True when activity-local date + HH:mm is still in the future.
 * Returns false when the region/inputs cannot be resolved (blocks submit).
 */
export function isFutureActivityLocalVisit(
  dateStr: string,
  timeHHmm: string,
  regionCode: RegionCode,
): boolean {
  const utcMs = utcMillisForActivityLocalVisit(dateStr, timeHHmm, regionCode);
  if (utcMs === null) {
    return false;
  }
  return utcMs > Date.now();
}
